import { useEffect, useState } from 'react';
import {
  Button,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag } from '../../../../../components';
import { getKeyPathValue, request, toast } from '../../../../../utils';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { BASE_URL_FOR_FILE } from '../../../../../utils/BaseUrl';
import axios from 'axios';
import { BASE_URL } from '../../../../../utils/BaseUrl';

const initialLabAuhtorityDoctorDataObj = {
  data: [],
  loading: false
};

const TABLE_COLUMNS = [
  {
    header: 'Doctor Name',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : status === 'default' ? 'blue' : 'black',
          }}
        >
          {`${row.doctor}`}
        </span>
      );
    },
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  },
  {
    header: 'Created At',
    accessor: 'createdAt'
  }
];

export const LabAuthorityDoctorMaster = () => {
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [labauthoritydoctorDataObj, setLabauthoritydoctorDataObj] = useState(initialLabAuhtorityDoctorDataObj);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const validationSchema = Yup.object().shape({
    doctor: Yup.string().required('Doctor Name is required'),
    heading_1: Yup.string(),
    heading_2: Yup.string(),
    heading_3: Yup.string(),
   

  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'all',
    defaultValues: {
      doctor: '',
      heading_1: '',
      heading_2: '',
      heading_3: '',
     
    }
  });

  const { handleSubmit, setValue, reset, watch } = methods;

  useEffect(() => {
    if (selectedRecord) {
      console.log("Selected Record:", selectedRecord);
      
      // Set basic fields
      setValue('doctor', selectedRecord.doctor || '');
      setValue('heading_1', selectedRecord.heading_1 || '');
      setValue('heading_2', selectedRecord.heading_2 || '');
      setValue('heading_3', selectedRecord.heading_3 || '');
     
      
      // Handle signature image
      if (selectedRecord.signature) {
        // Check if signature is an object or string
        let signatureFilename = '';
        if (typeof selectedRecord.signature === 'string') {
          signatureFilename = selectedRecord.signature;
        } else if (Array.isArray(selectedRecord.signature) && selectedRecord.signature[0]) {
          signatureFilename = selectedRecord.signature[0];
        } else if (selectedRecord.signature && selectedRecord.signature.filename) {
          signatureFilename = selectedRecord.signature.filename;
        }
        
        if (signatureFilename) {
          const imageUrl = `${BASE_URL_FOR_FILE}/${signatureFilename}`;
          setImagePreview(imageUrl);
        } else {
          setImagePreview(null);
        }
      } else {
        setImagePreview(null);
      }
      
      // Reset file state
      setImageFile(null);
    } else {
      reset();
      setImageFile(null);
      setImagePreview(null);
    }
  }, [setValue, reset, selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    const isEdit = selectedRecord && currentAction === 'edit';

    try {
      // Create FormData object
      const formData = new FormData();
      
      // Append all form values
      Object.entries(formValues).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, value);
        }
      });

      // Append the file if it exists
      if (imageFile) {
        formData.append('signature', imageFile);
      } else if (isEdit && selectedRecord.signature) {
        // If editing and no new file, pass the existing signature filename
        let existingSignature = '';
        if (typeof selectedRecord.signature === 'string') {
          existingSignature = selectedRecord.signature;
        } else if (Array.isArray(selectedRecord.signature) && selectedRecord.signature[0]) {
          existingSignature = selectedRecord.signature[0];
        }
        
        if (existingSignature) {
          formData.append('existingSignature', existingSignature);
        }
      }

      // Log FormData contents for debugging
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, value);
      }

      let response;
      if (isEdit) {
        // Use Axios for PUT request with FormData
        response = await axios.put(`${BASE_URL}/lab-authority-doctor/update/${selectedRecord._id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      } else {
        // Use Axios for POST request with FormData
        response = await axios.post(`${BASE_URL}/lab-authority-doctor/create`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      }

      console.log('Response:', response.data);

      if (response.data?.success) {
        toast(`Successfully ${isEdit ? 'updated' : 'created'} Lab Authority Doctor Master`, {
          variant: 'success'
        });

        if (isEdit) {
          setSelectedRecord(prev => ({ ...prev, ...response.data.data }));
        } else {
          setSelectedRecord(response.data.data);
        }

        setCurrentAction();
        reset();
        setImageFile(null);
      } else {
        toast(`Failed to ${isEdit ? 'update' : 'create'} Lab Authority Doctor Master: ${response.data?.error || 'Unknown error'}`, {
          variant: 'error'
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      
      let errorMessage = error.message;
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast(`Error: ${errorMessage}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRecord = async () => {
    if (!selectedRecord || !selectedRecord._id) {
      toast('No record selected to cancel', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const response = await request(`/lab-authority-doctor-cancel/${selectedRecord._id}`, {
        method: 'DELETE'
      });

      if (response?.success) {
        toast('Successfully cancelled Lab Authority Doctor Master', { variant: 'success' });
       
        
      } else {
        toast('Failed to cancel Lab Authority Doctor Master', { variant: 'error' });
      }
    } catch (error) {
      console.error('Cancel error:', error);
      toast('Error cancelling record', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRecord = async () => {
    if (!selectedRecord || !selectedRecord._id) {
      toast('No record selected to delete', { variant: 'warning' });
      return;
    }

    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await request(`/lab-authority-doctor-delete/${selectedRecord._id}`, {
        method: 'DELETE'
      });

      if (response?.success) {
        toast('Successfully deleted Lab Authority Doctor Master', { variant: 'success' });
        setSelectedRecord(null);
        reset();
        setImageFile(null);
        setImagePreview(null);
      } else {
        toast('Failed to delete Lab Authority Doctor Master', { variant: 'error' });
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast('Error deleting record', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setLabauthoritydoctorDataObj({ ...initialLabAuhtorityDoctorDataObj, loading: true });

    try {
      const response = await request('/lab-authority-doctor-list', {
        method: 'GET'
      });

      if (response.success) {
        // Filter out deleted records if needed
        const filteredData = response.data.filter(item =>
          item.status !== 'deleted'
        );

        setLabauthoritydoctorDataObj({
          loading: false,
          data: filteredData
        });
      } else {
        toast('Error in fetching data', { variant: 'error' });
        setLabauthoritydoctorDataObj({ loading: false, data: [] });
      }
    } catch (error) {
      console.error('Query error:', error);
      toast('Error fetching data', { variant: 'error' });
      setLabauthoritydoctorDataObj({ loading: false, data: [] });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp'];
      if (!validTypes.includes(file.type)) {
        toast('Please select a valid image file (JPEG, PNG, GIF, BMP)', { variant: 'error' });
        return;
      }

      // Validate file size (max 1MB)
      const maxSize = 1024 * 1024; // 1MB
      if (file.size > maxSize) {
        toast('File size should be less than 1MB', { variant: 'error' });
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearSignature = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleExit = () => {
    setCurrentAction('');
    setSelectedRecord(null);
    reset();
    setImageFile(null);
    setImagePreview(null);
  };

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: "100%",
            boxSizing: 'border-box',
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                Lab Authority Doctor Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord(null);
                    setCurrentAction('new');
                    reset();
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id="Submit"
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  {loading ? 'Saving...' : 'Save'}
                </CustomButtonn>
                <CustomButtonn
                  id="Cancel"
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || !selectedRecord || loading}
                  onClick={handleCancelRecord}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id="Delete"
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                  sx={{ backgroundColor: '#f44336', '&:hover': { backgroundColor: '#d32f2f' } }}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id="Exit"
                  variant="contained"
                  onClick={handleExit}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>

            <Box sx={{
              border: '1px solid',
              borderColor: 'grey.300',
              padding: 3,
              borderRadius: '8px',
              width: '60%',
              boxShadow: '0px 2px 10px rgba(0, 153, 255, 0.2)',
              backgroundColor: 'white'
            }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Doctor Name */}
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: "1rem" }}>
                      Doctor Name *
                    </Typography>
                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 0.8 }}
                      placeholder="Enter Doctor Name"
                      name="doctor"
                      size="small"
                      required
                    />
                  </Stack>

                  {/* Heading 1 */}
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: "1rem" }}>
                      Heading 1
                    </Typography>
                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 0.8 }}
                      name="heading_1"
                      size="small"
                      placeholder="Enter heading 1"
                    />
                  </Stack>

                  {/* Heading 2 */}
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: "1rem" }}>
                      Heading 2
                    </Typography>
                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 0.8 }}
                      name="heading_2"
                      size="small"
                      placeholder="Enter heading 2"
                    />
                  </Stack>

                  {/* Heading 3 */}
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: "1rem" }}>
                      Heading 3
                    </Typography>
                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 0.8 }}
                      name="heading_3"
                      size="small"
                      placeholder="Enter heading 3"
                    />
                  </Stack>

                  {/* Status */}
                 

                  {/* Signature Upload */}
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: "1rem" }}>
                      Signature
                    </Typography>
                    <Box sx={{ flex: 0.8, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          component="label"
                          disabled={!['new', 'edit'].includes(currentAction)}
                          size="small"
                          sx={{ minWidth: '150px' }}
                        >
                          Upload Signature
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleFileChange}
                          />
                        </Button>

                        {imageFile && (
                          <Typography variant="body2" sx={{ color: 'green' }}>
                            New file: {imageFile.name}
                          </Typography>
                        )}

                        {selectedRecord?.signature && !imageFile && (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Current signature exists
                          </Typography>
                        )}

                        {(imageFile || imagePreview) && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={clearSignature}
                            disabled={!['new', 'edit'].includes(currentAction)}
                          >
                            Clear
                          </Button>
                        )}
                      </Box>

                      {imagePreview && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                            Preview:
                          </Typography>
                          <img
                            src={imagePreview}
                            alt="Signature Preview"
                            style={{
                              width: '150px',
                              height: '80px',
                              borderRadius: '4px',
                              border: '1px solid #ccc',
                              objectFit: 'contain',
                              backgroundColor: '#f5f5f5'
                            }}
                          />
                        </Box>
                      )}

                      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Supported formats: JPEG, PNG, GIF, BMP (Max 1MB)
                      </Typography>
                    </Box>
                  </Stack>

                  
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>

      {/* Query Dialog */}
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', maxWidth: '1200px', p: 1.5, maxHeight: '80vh' } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Lab Authority Doctor Master Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)} size="large">
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>

        <Box sx={{ mt: 2, overflow: 'auto' }}>
          <Table
            columns={TABLE_COLUMNS}
            data={labauthoritydoctorDataObj.data || []}
            loading={labauthoritydoctorDataObj.loading}
            onRowClick={(row) => {
              console.log('Row selected:', row);
              setOpenQueryDialog(false);
              setSelectedRecord(row);
              setCurrentAction('');
              
              // Set image preview if signature exists
              if (row.signature) {
                let signatureFilename = '';
                if (typeof row.signature === 'string') {
                  signatureFilename = row.signature;
                } else if (Array.isArray(row.signature) && row.signature[0]) {
                  signatureFilename = row.signature[0];
                }
                
                if (signatureFilename) {
                  const imageUrl = `${BASE_URL_FOR_FILE}/${signatureFilename}`;
                  setImagePreview(imageUrl);
                } else {
                  setImagePreview(null);
                }
              } else {
                setImagePreview(null);
              }
            }}
            sx={{ minWidth: 800 }}
          />
        </Box>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'grey.300' }}>
          <Button
            variant="outlined"
            onClick={() => setOpenQueryDialog(false)}
            sx={{ minWidth: '100px' }}
          >
            Close
          </Button>
        </Stack>
      </Dialog>
    </>
  );
};